import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import swal from 'sweetalert'
import { useNavigate } from 'react-router-dom'
import CourseDetailsComp from './CourseDetailsComp'
import useAcademicCourses, { formatDateValue, formatDuration, formatIntakeLabel } from './useAcademicCourses'

function AcademicComp() {
  const navigate = useNavigate()
  const {
    isLoading,
    canAddSecondaryCourse,
    courseRecords
  } = useAcademicCourses()
  const [activeCourseName, setActiveCourseName] = useState('')

  useEffect(() => {
    if (!courseRecords.length) {
      setActiveCourseName('')
      return
    }

    setActiveCourseName((current) => (
      courseRecords.some((course) => course.name === current) ? current : courseRecords[0].name
    ))
  }, [courseRecords])

  const activeCourse = useMemo(
    () => courseRecords.find((course) => course.name === activeCourseName) || courseRecords[0] || null,
    [activeCourseName, courseRecords]
  )

  const handleOpenAddCourse = () => {
    if (!canAddSecondaryCourse) {
      swal('Unavailable', 'Only students can add a new course from this page.', 'info')
      return
    }
    navigate('/add-course')
  }

  return (
    <div className='academicWorkspacePage'>
      <div className='academicWorkspaceHeader'>
        <div className='academicWorkspaceCopy'>
          <span className='academicWorkspaceEyebrow'>My Courses</span>
          <h4>{activeCourse?.name || 'Course workspace'}</h4>
          <p>{activeCourse?.description || 'Open the active course details here, then switch courses from the selector on the right whenever you need to move across programs.'}</p>

        </div>

        <div className='academicWorkspaceControls'>
          <span className='academicWorkspaceControlsLabel'>Courses</span>
          <div className='academicCourseSwitchRow'>
            <div className='academicCourseSwitchList'>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <span className='academicCourseSwitch academicCourseSwitchSkeleton' key={`course-pill-${index}`} />
                ))
              ) : (
                courseRecords.map((course) => (
                  <button
                    key={course.name}
                    type='button'
                    className={`academicCourseSwitch ${activeCourseName === course.name ? 'active' : ''}`}
                    onClick={() => setActiveCourseName(course.name)}
                  >
                    {course.name}
                  </button>
                ))
              )}
            </div>

            <button
              type='button'
              className='academicCourseAddRound'
              onClick={handleOpenAddCourse}
              aria-label='Add a course'
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>

          {!canAddSecondaryCourse && (
            <p className='academicWorkspaceHint'>The add-course option appears here once your status is Student.</p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className='academicWorkspaceLoading'>
          <div className='academicWorkspaceLoadingHero' />
          <div className='academicWorkspaceLoadingTabs'>
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className='academicWorkspaceLoadingPanel' />
        </div>
      ) : activeCourse ? (
        <CourseDetailsComp
          embedded
          forcedCourseName={activeCourse.name}
          courseSummary={{
            track: activeCourse.type === 'secondary' ? 'Secondary Course' : 'Main Course',
            status: activeCourse.status || 'Pending',
            durationLabel: activeCourse.type === 'secondary' ? 'Intake' : 'Duration',
            durationValue: activeCourse.type === 'secondary' ? formatIntakeLabel(activeCourse.intake) : formatDuration(activeCourse.duration),
            admission: formatDateValue(activeCourse.admissionDate)
          }}
        />
      ) : (
        <div className='academicWorkspaceEmpty'>
          <h5>No course found yet</h5>
          <p>We could not find an active course on your profile yet. Please check back shortly or contact the admissions team if this looks unexpected.</p>
        </div>
      )}
    </div>
  )
}

export default AcademicComp
